<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import { formatPhoneNumber } from '@/utils/phoneNumberValidator'
import { onlyNumbersValidator } from '@/utils/onlyNumbersValidator'
import ErrorAlert from '@/components/ErrorAlert.vue'
import InfoAlert from '@/components/InfoAlert.vue'
import {
  formatCardNumber,
  formatCardValidity,
} from '@/utils/creditCardValidator'
import { useThemeStore } from '@/stores/darkThemeStore'

export default defineComponent({
  name: 'CustomInput',
  components: {
    ErrorAlert,
    InfoAlert,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: String,
      required: true,
    },
    inputMode: {
      type: String,
      default: 'text',
    },
    maxlength: {
      type: [String, Number],
      default: 255,
    },
    type: {
      type: String,
      default: 'text',
    },
    name: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    onlyNumbers: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    errorClass: {
      type: String,
      default: '',
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    darkClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'input'],
  setup(props, { emit }) {
    const inputValue = ref(props.modelValue)
    const themeStore = useThemeStore()
    const theme = themeStore.theme

    watch(inputValue, (newVal) => {
      emit('update:modelValue', newVal)
    })

    const inputClass = computed(() => {
      return [
        props.customClass,
        {
          [props.errorClass]: props.hasError,
          [props.darkClass]: props.isDark,
        },
      ]
    })
    const labelClass = computed(() => {
      return ['custom-label', props.isDark ? 'dark-custom-label' : '']
    })

    const currentValue = computed({
      get(): string {
        if (props.format === 'phone') {
          return formatPhoneNumber(inputValue.value)
        }
        else if (props.format === 'card-number') {
          return formatCardNumber(inputValue.value)
        }
        else if (props.format === 'card-validity') {
          return formatCardValidity(inputValue.value)
        }
        else {
          return inputValue.value
        }
      },
      set(value: string) {
        inputValue.value = value
        emit('update:modelValue', value)
      },
    })

    const onKeypress = (event: KeyboardEvent) => {
      if (props.onlyNumbers) {
        return onlyNumbersValidator(event)
      }
    }

    const onInput = (event: Event) => {
      if (
        props.id === 'phone'
        || props.id === 'card-number'
        || props.id === 'card-validity'
        || props.id === 'card-code'
      ) {
        const target = event.target as HTMLInputElement
        emit('input', target.value)
      }
      else {
        emit('input', inputValue.value)
      }
    }

    return {
      currentValue,
      onInput,
      onKeypress,
      theme,
      inputClass,
      labelClass,
    }
  },
})
</script>

<template>
  <div class="inputBlock">
    <input
      :id="id"
      v-model="currentValue"
      :inputmode="inputMode"
      :maxlength="maxlength"
      :type="type"
      :name="name"
      :autocomplete="autocomplete"
      :class="inputClass"
      :style="{
        padding: currentValue ? '25px 16px 9px' : '0 16px',
      }"
      :placeholder="label"
      @keypress="onKeypress"
      @input="onInput"
    >

    <label v-if="currentValue.length > 0" :for="id" :class="labelClass">{{
      label
    }}</label>
    <small v-if="id === 'pin'">
      <ErrorAlert v-if="errorMessage" :message="errorMessage" />
      <InfoAlert v-else :message="$t('phoneNumberForm.pinInfoDesc')" />
    </small>
    <small v-else-if="id === 'card-number'">
      <ErrorAlert v-if="errorMessage" :message="errorMessage" />
    </small>
    <small v-else>
      <span class="error-message">{{ errorMessage }}</span>
    </small>
  </div>
</template>

<style scoped></style>
